/**
 * useInboxCockpitState — owns the data + UI state for the three-pane Inbox cockpit.
 *
 * Combines the existing /api/work/inbox and /api/work/summary fetchers,
 * exposes selectedId + filterTile state, and computes a one-line takeaway sentence
 * from the loaded items + summary. The cockpit components are presentational;
 * this hook is the source of truth.
 *
 * Truth chain: docs/agents/pitch-deck-review/pages/DEAL_EVALUATOR_PAGE_SPEC_V2.md
 *              Section 16 (WI-UI-CLEANUP-001 Stage 2).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCanonicalInbox,
  getInboxSummary,
  getIdeaQueue,
  approveCampaignTask,
  rejectCampaignTask,
  saveArticleToKB,
  rejectArticle,
  overrideArticle,
  passDeal,
  contactDeal,
} from '@/lib/api';
import { REASON_LABELS } from '@/config/decisionConstants';
import { WORK_TYPES } from '@/config/task-display-config';
import { resolveAcceptOutcome } from './acceptStatusOutcomes';
import { toast } from 'sonner';

const normalizeItem = (raw) => ({
  id: raw.id,
  title: raw.display_name || raw.title,
  description: raw.description,
  priority: raw.priority,
  status: raw.status,
  work_type: raw.work_type,
  recommendation: raw.recommendation || raw.payload?.recommendation || 'review',
  payload: raw.payload || {},
  created_at: raw.timestamp,
  actions: raw.actions || [],
});

function buildTakeaway(items, summary, loadFailed, ideaGroups) {
  // WI-POST-STUDIO-PENDING-VIEW-AND-ONE-CARD-001 Leg 0b (CEO ruling 2026-07-30).
  // THE CALM PHRASE IS A CLAIM, and it must not be made when the list never arrived.
  // `getCanonicalInbox` used to swallow a failure into an empty list, so this line stated
  // "nothing waiting on you" while two items sat on the server — observed live. The client now
  // throws, but the takeaway still has to tell the two apart, or the screen contradicts its own
  // error bar: banner says all clear, bar says it could not load. That contradiction is exactly
  // what P-97 named on the counters.
  if (loadFailed) {
    return 'The inbox could not be loaded, so nothing is being shown. This is not the same as having nothing waiting.';
  }
  // Empty inbox — WI-CONTENT-LANE-ROBUSTNESS-001 Leg E2 (CEO: "the inbox can never render a
  // bare empty state when cards exist in a withheld/held-back condition — it must say what
  // is there and why"). A card an idea-quality check has withheld drops OUT of this list by
  // design (assemble_canonical_inbox's `_withhold_incomplete_idea_card` path) and resurfaces
  // ONLY on the separate Ideas tab (tab_definitions.py's `held_back` group). With the content
  // lane's whole live population small enough to hand-count, that withhold alone can empty
  // THIS screen while something genuinely needs him one tab over — "Inbox is clear" would be
  // true about this list and false about his situation. Check the held-back group first: it is
  // the one that needs him. `holds` is tab_definitions.py's own CEO-facing sentence, reused
  // verbatim rather than re-authored here — one vocabulary, not two.
  if (!items || items.length === 0) {
    const heldBack = ideaGroups?.find((g) => g.id === 'held_back' && (g.count || 0) > 0);
    if (heldBack) {
      const n = heldBack.count;
      return (
        `No decisions are waiting in this inbox. But ${n} ${n === 1 ? 'idea is' : 'ideas are'} ` +
        `held back on the Ideas tab: ${heldBack.holds}`
      );
    }
    // Nothing held back, but a card can still be alive-and-invisible the OTHER way: approved
    // and queued for drafting, needing nothing from him yet, but proof the lane is not dead.
    // The Meesho card sat in this exact state for thirteen days with no surface saying so.
    const queued = ideaGroups?.find(
      (g) => g.id === 'queued_for_drafting' && (g.count || 0) > 0,
    );
    if (queued) {
      const n = queued.count;
      return (
        `Inbox is clear. Nothing waiting on you right now. ${n} ${n === 1 ? 'idea is' : 'ideas are'} ` +
        `queued for drafting — ${queued.holds}`
      );
    }
    return 'Inbox is clear. Nothing waiting on you right now.';
  }
  const strong = summary?.strong?.count ?? 0;
  const weak = summary?.weak?.count ?? 0;
  const pass = summary?.pass?.count ?? 0;

  const parts = [];
  if (strong > 0) {
    parts.push(`${strong} worth ${strong === 1 ? 'a call' : 'calls'}`);
  }
  if (weak > 0) {
    parts.push(`${weak} ${weak === 1 ? 'needs' : 'need'} a second read`);
  }
  if (pass > 0) {
    parts.push(`${pass} ${pass === 1 ? 'pass' : 'passes'} cleared`);
  }
  if (parts.length === 0) {
    return `${items.length} ${items.length === 1 ? 'decision' : 'decisions'} waiting for your read.`;
  }
  // Sentence-case the first letter and end with a period.
  const sentence = parts.join(', ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1) + '.';
}

export function useInboxCockpitState() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [summaryError, setSummaryError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [filterTile, setFilterTile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The Ideas tab's own group counts (held_back / queued_for_drafting), read ONLY so this
  // screen's empty state can say what tab_definitions.py already knows is true elsewhere —
  // see buildTakeaway. `null` means "unknown" (not yet loaded, or the fetch failed), and is
  // deliberately never treated as "nothing held back": an unknown must never be read as a
  // zero the same way `null !== []` for `items` above.
  const [ideaQueueGroups, setIdeaQueueGroups] = useState(null);

  // The list, the summary and the idea-queue signal come from three different endpoints and
  // are settled INDEPENDENTLY (allSettled, not all). Any one can fail without taking the
  // others down, and each failure is surfaced on its own — a dead summary must not blank the
  // list, and it must never be rendered as seven zeros.
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSummaryError(null);
    const [inboxResult, summaryResult, ideaQueueResult] = await Promise.allSettled([
      getCanonicalInbox(),
      getInboxSummary(),
      getIdeaQueue(),
    ]);

    if (inboxResult.status === 'fulfilled') {
      const normalized = (inboxResult.value?.items || []).map(normalizeItem);
      setItems(normalized);
      // Auto-select the first item on initial load if nothing is selected yet.
      setSelectedId((current) => current ?? (normalized.length > 0 ? normalized[0].id : null));
    } else {
      console.error('[InboxCockpit] inbox load failed:', inboxResult.reason);
      setError('Could not load inbox. Try again.');
    }

    if (summaryResult.status === 'fulfilled') {
      setSummary(summaryResult.value || {});
    } else {
      console.error('[InboxCockpit] summary load failed:', summaryResult.reason);
      setSummary({});
      setSummaryError(
        summaryResult.reason?.message || 'The summary figures could not be loaded.',
      );
    }

    if (ideaQueueResult.status === 'fulfilled') {
      setIdeaQueueGroups(ideaQueueResult.value?.groups || []);
    } else {
      // Silent by design here, unlike the two loads above: this signal only ever ADDS a
      // sentence to an otherwise-true empty state (see buildTakeaway). Losing it means the
      // empty state falls back to the plain calm phrase rather than showing an error the CEO
      // did not ask for on a screen that, as far as its own list is concerned, really is
      // empty. Logged for a terminal to find, never surfaced as a banner.
      console.error('[InboxCockpit] idea queue signal load failed:', ideaQueueResult.reason);
      setIdeaQueueGroups(null);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Clear selection if the selected item is no longer in the list (e.g., after
  // an action that removes it).
  useEffect(() => {
    if (selectedId && !items.find((i) => i.id === selectedId)) {
      setSelectedId(items.length > 0 ? items[0].id : null);
    }
  }, [items, selectedId]);

  const selected = useMemo(
    () => items.find((i) => i.id === selectedId) || null,
    [items, selectedId],
  );

  const takeawayText = useMemo(
    () => buildTakeaway(items, summary, Boolean(error), ideaQueueGroups),
    [items, summary, error, ideaQueueGroups],
  );

  // ── Action callbacks ────────────────────────────────────────────────────────
  //
  // WI-INBOX-ROUTING-001 Stage 1: the cockpit's detail renderers consume
  // three callbacks (onArticleAction / onAccept / onReject). The old
  // Inbox.jsx owned these handlers; they were dropped in Stage 2 of
  // WI-UI-CLEANUP-001 and are re-added here so renderers can action items
  // from inside the cockpit. API endpoints are unchanged.

  // Returns the message it showed, so callers can announce the SAME words to a
  // screen reader — the CEO's primary channel must not disagree with the toast.
  const showGenericError = (err) => {
    const is503 =
      err?.status === 503 ||
      err?.response?.status === 503 ||
      err?.message?.includes('503');
    // WI-GATEENVELOPE-SILENT-200-AUDIT-AND-FIX-001 (L-055): prefer the backend's
    // own business-facing sentence (response_taxonomy is the message authority)
    // over this generic fallback — it tells the CEO what to actually do next
    // ("refresh to see the current state"). Raw `HTTP 500: ...` strings from
    // api.js's fallback are tech jargon and are deliberately NOT surfaced
    // (CLAUDE.md rules 17-18).
    const backendMessage =
      err?.message && !/^HTTP \d{3}:/.test(err.message) ? err.message : null;
    const message = is503
      ? 'System is syncing — please try again in a moment'
      : backendMessage || 'Something went wrong, please try again';
    toast.error(message);
    return message;
  };

  const onAccept = useCallback(
    async (id, workType, payload) => {
      try {
        const data = await approveCampaignTask(id, payload);

        // WI-IDEA-LANE-EXECUTE-CEO-RULINGS-001 item A (CEO ruling 2026-07-30).
        // This used to be an inline chain: 'approved' -> success, 'ignored' -> already
        // processed, **else -> error**. That is an ALLOW-LIST, so every status literal the
        // route grew silently joined the error branch — and one did. The failure-receipt
        // acknowledge action returns "acknowledged" AFTER a write that lands, so the CEO was
        // shown "Something went wrong, please try again" over a successful write, and the list
        // never refreshed because refresh() lived only in the 'approved' branch. He reported
        // his buttons as dead; they were not. The closed vocabulary now lives in
        // acceptStatusOutcomes.js with a test that enumerates every literal the route can
        // return, so the NEXT one cannot repeat this quietly.
        const outcome = resolveAcceptOutcome(data);
        if (outcome.success) {
          let successMsg = outcome.message;
          // The work-type/action-specific sentence beats the generic one where there is one.
          if (workType === WORK_TYPES.CAMPAIGN) {
            successMsg = 'Approved and queued for delivery';
          } else if (
            workType === WORK_TYPES.LINKEDIN_POST &&
            payload?.action === 'mark_published'
          ) {
            successMsg = 'Marked as published';
          } else if (
            workType === WORK_TYPES.LINKEDIN_POST &&
            payload?.action === 'save_draft'
          ) {
            successMsg = 'Draft saved';
          }
          toast.success(successMsg);
        } else {
          toast.error(outcome.message);
        }
        // Declared per outcome rather than inferred from success — "did it work" and "must the
        // list be re-read" are different questions that happen to agree today.
        if (outcome.refresh) {
          refresh();
        }
      } catch (err) {
        console.error('[useInboxCockpitState] accept failed:', err);
        showGenericError(err);
        // WI-GATEENVELOPE-SILENT-200-AUDIT-AND-FIX-001 (L-055) — the second
        // swallow. Backend honesty alone was not enough: this catch used to
        // absorb the error and resolve normally, so a caller's own catch could
        // never run. PreDraftIdeaCard then announced "Idea rejected — reason
        // recorded" over aria-live on a 500, while the toast said the opposite.
        // The CEO's primary channel was told the lie out loud.
        // Rethrow, exactly as onPassDeal/onContacted already do below.
        throw err;
      }
    },
    [refresh],
  );

  const onReject = useCallback(
    async (id, reasonCode, workType, calibration, attribution) => {
      try {
        const label = REASON_LABELS[reasonCode?.toUpperCase?.()] || reasonCode;
        let data;
        if (workType === WORK_TYPES.ARTICLE_REVIEW) {
          const item = items.find((i) => i.id === id);
          const articleId = item?.payload?.article_id || id;
          data = await rejectArticle(articleId, label);
          if (data) {
            toast.success('Article dismissed');
            refresh();
            return;
          }
        } else {
          // WI-LINKEDIN-IDEA-GATE-OBSERVE-BUILD-001: attribution is optional and
          // additive — trains the idea side (message_wrong) vs. the writing side
          // (writing_wrong) for linkedin_post rejects; undefined for every other
          // work type, which rejectCampaignTask omits from the request body.
          //
          // WI-LINKEDIN-REJECT-REASONS-AND-LEARNING-LOOP-001: linkedin_post reason
          // codes come from the taxonomy at GET /api/linkedin-posts/reject-reasons
          // (e.g. `timing`) and must be persisted RAW, not translated through the
          // shared REASON_LABELS map — `timing`.toUpperCase() === 'TIMING' collides
          // with REASON_LABELS.TIMING ("Timing not right") from decisionConstants.js,
          // which would silently overwrite the taxonomy's own reason label. Every
          // other work type keeps the existing translated `label`.
          const rejectionReason = workType === WORK_TYPES.LINKEDIN_POST ? reasonCode : label;
          data = await rejectCampaignTask(id, rejectionReason, calibration, attribution);
        }
        if (data?.status === 'rejected') {
          toast.success(
            workType === WORK_TYPES.LINKEDIN_POST
              ? 'Post rejected'
              : 'Item rejected',
          );
          refresh();
        } else {
          toast.error(data?.reason || 'Something went wrong, please try again');
        }
      } catch (err) {
        console.error('[useInboxCockpitState] reject failed:', err);
        showGenericError(err);
        throw err; // See the DECISION in onAccept above (L-055).
      }
    },
    [items, refresh],
  );

  // Tracxn-specific Pass handler — G4-FIX-001. Fires only the tracxn endpoint;
  // the universal accept endpoint is NOT called, so the previous double-fire
  // (passDeal + approveCampaignTask) is eliminated.
  // WI-SHARED-DEAL-CARD-001: forwards the optional `reason` (selected from
  // the Pass dropdown) so the backend persists pass_reason and pass_reason_at.
  const onPassDeal = useCallback(
    async (dealId, reason = null) => {
      if (!dealId) return;
      try {
        await passDeal(dealId, reason);
        toast.success('Deal passed');
        refresh();
      } catch (err) {
        console.error('[useInboxCockpitState] passDeal failed:', err);
        showGenericError(err);
        // Leg 5 (a11y, WI-TRACXN-ENRICHMENT-BUILD-LEGS-3-7-001): rethrow so
        // DealCard's own try/catch can tell its aria-live announcer this
        // failed — a toast alone is not reliably announced to a screen
        // reader. DealSourcingTaskDetail's adapter has no catch of its own,
        // so this propagates straight through to DealCard unchanged.
        throw err;
      }
    },
    [refresh],
  );

  // Tracxn-specific Contact handler — WI-SHARED-DEAL-CARD-001 PR D.
  // Removes the matching work item optimistically before the network call so
  // the card disappears immediately on click; re-fetches on error to restore
  // state if the server rejected. The shared DealCard fires this via the
  // adapter on the inbox surface.
  const onContacted = useCallback(
    async (workItemId, dealId) => {
      if (!dealId) return;
      let removed = null;
      setItems((prev) => {
        const idx = prev.findIndex((i) => i.id === workItemId);
        if (idx >= 0) {
          removed = prev[idx];
          return prev.filter((i) => i.id !== workItemId);
        }
        return prev;
      });
      try {
        await contactDeal(dealId);
        toast.success('Marked as contacted.');
        refresh();
      } catch (err) {
        console.error('[useInboxCockpitState] contactDeal failed:', err);
        // Roll back the optimistic removal so the CEO sees the card again.
        if (removed) {
          setItems((prev) => [removed, ...prev]);
        }
        showGenericError(err);
        throw err; // See DECISION in onPassDeal above.
      }
    },
    [refresh],
  );

  const onArticleAction = useCallback(
    async (articleId, action, reason, calibration) => {
      try {
        if (action === 'save') {
          await saveArticleToKB(articleId, calibration);
          toast.success('Article saved to Knowledge Base');
        } else if (action === 'reject') {
          await rejectArticle(articleId, reason, calibration);
          toast.success('Article rejected');
        } else if (action === 'override') {
          await overrideArticle(articleId, reason, calibration);
          toast.success('Article overridden and saved to Knowledge Base');
        }
        refresh();
      } catch (err) {
        console.error('[useInboxCockpitState] article action failed:', err);
        showGenericError(err);
      }
    },
    [refresh],
  );

  return {
    items,
    summary,
    summaryError,
    selected,
    selectedId,
    setSelectedId,
    filterTile,
    setFilterTile,
    loading,
    error,
    refresh,
    takeawayText,
    onAccept,
    onReject,
    onArticleAction,
    onPassDeal,
    onContacted,
  };
}

export default useInboxCockpitState;
